import { createContext, useContext, useEffect, useMemo, useState } from "react";

import { activeCheckoutEntitlementGrants } from "./checkout-entitlements";
import { EntitlementProvider, initialEntitlementStateForRuntime } from "./entitlement-context";
import type { PaidFeatureId } from "./paid-features";
import type { EntitlementState } from "./paid-features";
import { CURRENT_ENTITLEMENT_STATE } from "./paid-features";
import {
  type BrowserPurchaseAccessEvidenceKind,
  type PurchaseAccessEvidenceConfigResult,
  purchaseAccessEvidenceConfigFromVerificationConfig,
  recordPurchaseAccessEvidence,
} from "./purchase-access-evidence-client";
import { readOrCreatePurchaseInstallId } from "./purchase-installation-id";
import {
  type PurchaseVerificationConfigResult,
  type PurchaseVerificationResult,
  parsePurchaseVerificationConfig,
  restorePurchaseVerification,
} from "./purchase-verification-client";

export type PurchaseRestoreStatus =
  | { state: "not_configured" }
  | { state: "checking" }
  | { state: "restored" }
  | {
      state: "failed_closed";
      reason: Extract<PurchaseVerificationResult, { ok: false }>["reason"];
    };

const PurchaseRestoreStatusContext = createContext<PurchaseRestoreStatus>({
  state: "not_configured",
});

type PurchaseAccessEvidenceState =
  | { state: "not_configured" }
  | {
      state: "ready";
      config: PurchaseAccessEvidenceConfigResult;
      installId: string;
      grant: ReturnType<typeof activeCheckoutEntitlementGrants>[number];
    };

const PurchaseAccessEvidenceContext = createContext<PurchaseAccessEvidenceState>({
  state: "not_configured",
});

export function VerifiedEntitlementProvider({
  children,
  config,
}: {
  children: React.ReactNode;
  config?: PurchaseVerificationConfigResult;
}) {
  const initialState = useMemo(() => initialEntitlementStateForRuntime(), []);
  const verificationConfig = useMemo(
    () => config ?? parsePurchaseVerificationConfig(import.meta.env.VITE_PURCHASE_VERIFICATION_URL),
    [config],
  );
  const [state, setState] = useState<EntitlementState>(initialState);
  const [restoreStatus, setRestoreStatus] = useState<PurchaseRestoreStatus>(() =>
    initialState.source === "verified_checkout"
      ? { state: "restored" }
      : verificationConfig.type === "configured"
        ? { state: "checking" }
        : { state: "not_configured" },
  );
  const [accessEvidence, setAccessEvidence] = useState<PurchaseAccessEvidenceState>({
    state: "not_configured",
  });

  useEffect(() => {
    if (initialState.source === "verified_checkout") {
      setRestoreStatus({ state: "restored" });
      setAccessEvidence({ state: "not_configured" });
      return;
    }
    if (verificationConfig.type !== "configured") {
      setState(CURRENT_ENTITLEMENT_STATE);
      setRestoreStatus({ state: "not_configured" });
      setAccessEvidence({ state: "not_configured" });
      return;
    }

    let cancelled = false;
    setRestoreStatus({ state: "checking" });
    setAccessEvidence({ state: "not_configured" });
    const installId = readOrCreatePurchaseInstallId();
    restorePurchaseVerification({ config: verificationConfig, installId }).then((result) => {
      if (cancelled) return;
      setState(result.ok ? result.state : CURRENT_ENTITLEMENT_STATE);
      setRestoreStatus(
        result.ok ? { state: "restored" } : { state: "failed_closed", reason: result.reason },
      );
      if (!result.ok) {
        setAccessEvidence({ state: "not_configured" });
        return;
      }
      const grant = preferredAccessEvidenceGrant(
        activeCheckoutEntitlementGrants({
          grants: result.snapshot.grants,
          revokedProviderPurchases: result.snapshot.revokedProviderPurchases,
        }),
      );
      setAccessEvidence(
        grant
          ? {
              state: "ready",
              config: purchaseAccessEvidenceConfigFromVerificationConfig(verificationConfig),
              installId: result.installId,
              grant,
            }
          : { state: "not_configured" },
      );
    });

    return () => {
      cancelled = true;
    };
  }, [verificationConfig, initialState.source]);

  return (
    <PurchaseRestoreStatusProvider value={restoreStatus}>
      <PurchaseAccessEvidenceContext.Provider value={accessEvidence}>
        <EntitlementProvider value={state}>{children}</EntitlementProvider>
      </PurchaseAccessEvidenceContext.Provider>
    </PurchaseRestoreStatusProvider>
  );
}

export function PurchaseRestoreStatusProvider({
  value,
  children,
}: {
  value: PurchaseRestoreStatus;
  children: React.ReactNode;
}) {
  return (
    <PurchaseRestoreStatusContext.Provider value={value}>
      {children}
    </PurchaseRestoreStatusContext.Provider>
  );
}

export function usePurchaseRestoreStatus(): PurchaseRestoreStatus {
  return useContext(PurchaseRestoreStatusContext);
}

export function usePurchaseAccessEvidenceRecorder(featureId?: PaidFeatureId) {
  const accessEvidence = useContext(PurchaseAccessEvidenceContext);

  return async (kind: BrowserPurchaseAccessEvidenceKind): Promise<void> => {
    if (accessEvidence.state !== "ready") return;
    await recordPurchaseAccessEvidence({
      config: accessEvidence.config,
      grant: accessEvidence.grant,
      installId: accessEvidence.installId,
      kind,
      featureId,
    });
  };
}

function preferredAccessEvidenceGrant(grants: ReturnType<typeof activeCheckoutEntitlementGrants>) {
  return grants.find((grant) => grant.plan === "lifetime") ?? grants[0];
}
